import { DatePicker } from 'antd';
import oddsApi from 'api/oddsApi';
import { useAppDispatch, useAppSelector, useAppTranslation } from 'app/hooks';
import EmptyTableIcon from 'assets/icons/EmptyTableIcon';
import axios, { CancelTokenSource } from 'axios';
import LoadingSpinner from 'components/Commons/LoadingSpinner';
import Typo from 'consts/typography';
import { selectFetchMyBet, setFetchMyBet } from 'features/home/homeSlice';
import useUserDetail from 'hooks/useUserDetail';
import { BetResponseModel, PnLResponse } from 'models';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { BsEye, BsEyeSlash } from 'react-icons/bs';
import { platformStates } from 'redux/platformSlice';
import { isLaptop, isPhone, isTablet } from 'utils/commonUtils';
import { formatAmountV2 } from 'utils/formatAmount';
import MyBetComboItem from './MyBetComboItem';
import MybetSingleItem from './MybetSingleItem';
import MyBetsTab from './MyBetsTab';
import MyBetTeaserItem from './MyBetTeaserItem';
import ProfitAndLoss from './ProfitAndLoss';

interface MyBetsFormProps {}

const MyBetsForm: React.FunctionComponent<MyBetsFormProps> = () => {
  const { RangePicker } = DatePicker;
  const isFetchMyBet = useAppSelector(selectFetchMyBet);
  const dispatch = useAppDispatch();
  const minToDate = moment('2023-01-01 00:00');
  const maxDate = moment(new Date());
  const { currentUser } = useUserDetail();
  const t = useAppTranslation();
  const platform = useAppSelector(platformStates);

  const [currentTab, setCurrentTab] = React.useState<number>(1);
  const [bets, setBets] = React.useState<BetResponseModel[] | null>(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [pnl, setPnl] = useState<PnLResponse>();
  const [atr, setAtr] = useState<PnLResponse>();
  const [showAtr, setShowAtr] = useState<boolean>(false);
  const [cancelTokenSource, setCancelTokenSource] = useState<CancelTokenSource | null>(null);
  const [profitOrLoss, setProfitOrLoss] = useState(0);

  const getBets = useCallback(async () => {
    const source = axios.CancelToken.source();
    setCancelTokenSource(source);
    const { ok, body } = await oddsApi.getBets(
      {
        page: 0,
        size: 999,
        sort: 'createdDate,desc',
        'createdDate.greaterThanOrEqual': fromDate,
        'createdDate.lessThanOrEqual': toDate,
        ...(currentTab === 1 ? { 'status.in': 'WAITING' } : { 'status.notIn': 'WAITING' }),
      },
      source.token,
    );
    if (ok && body) {
      setBets(body);
      if (fromDate !== '' && toDate !== '') {
        const totalWinAmount = body
          .filter((bet) => bet.status === 'WIN')
          .reduce((sum, bet) => sum + (bet.winAmount || 0), 0);

        const totalLoseAmount = body
          .filter((bet) => bet.status === 'LOSE')
          .reduce((sum, bet) => sum + (bet.amount || 0), 0);
        const profitOrLoss = totalWinAmount - totalLoseAmount;
        setProfitOrLoss(profitOrLoss);
      }
    }
  }, [fromDate, toDate, currentTab]);

  const getPnl = async () => {
    const { ok, body } = await oddsApi.getBetsPnl();
    if (ok && body) {
      setPnl(body);
    }
  };

  const getAtr = async () => {
    const { ok, body } = await oddsApi.getBetsATR();
    if (ok && body) {
      setAtr(body);
    }
  };

  const changeTab = (tab: number): void => {
    setFromDate('');
    setToDate('');
    setProfitOrLoss(0);
    if (currentTab !== tab) {
      setCurrentTab(tab);
    }
  };

  useEffect(() => {
    if (currentUser) {
      getPnl();
      getAtr();
    }
  }, [currentUser?.id]);

  useEffect(() => {
    setBets(null);
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Request canceled due to new tab');
    }
    getBets();
  }, [getBets]);

  useEffect(() => {
    if (isFetchMyBet) {
      dispatch(setFetchMyBet(false));
      if (currentTab === 1) {
        getBets();
      }
    }
  }, [isFetchMyBet]);

  const handleFromDateChange = (date: Moment | null) => {
    const dateFormat = moment(date).startOf('day').toISOString();
    return dateFormat;
  };

  const handleToDateChange = (date: Moment | null) => {
    const dateFormat = moment(date).endOf('day').toISOString();
    return dateFormat;
  };

  const handleShowAtr = () => {
    setShowAtr((prev) => !prev);
  };

  const handleDateChange = useCallback(
    (date: [moment.Moment | null, moment.Moment | null] | null) => {
      setFromDate(handleFromDateChange(date?.[0] ? date[0].startOf('day') : null));
      setToDate(handleToDateChange(date?.[1] ? date[1].endOf('day') : null));
    },
    [setFromDate, setToDate],
  );

  return (
    <div className="my-bets-form">
      <MyBetsTab currentTab={currentTab} changeTab={changeTab} />
      {currentTab === 2 && (
        <div>
          {!(isLaptop(platform) || isTablet(platform) || isPhone(platform)) ? (
            <div className="pnl-container">
              <div className="pnl-mobile">
                <div className="flex flex-row items-center pnl-container w-1/2">
                  <span className={`primary-text-color ${Typo.p5} font-bold`}>
                    {t('pnl.title')} :
                  </span>
                  {pnl && <ProfitAndLoss pnlType="TODAY" pnl={pnl} />}
                </div>
                <div className="flex flex-row items-center pnl-container w-1/2">
                  <div className="flex flex-row items-center">
                    <span className={`primary-text-color ${Typo.p5} font-bold`}>ATR :</span>
                    {/* <button className="hidden-show-button primary-text-color" onClick={handleShowAtr}>
                      {showAtr ? <BsEyeSlash /> : <BsEye />}
                    </button> */}
                  </div>
                  {atr && (
                    <div className="flex flex-row">
                      <button
                        className="hidden-show-button primary-text-color"
                        onClick={handleShowAtr}
                      >
                        {showAtr ? <BsEyeSlash /> : <BsEye />}
                      </button>{' '}
                      <ProfitAndLoss pnlType="ATR" pnl={atr} showAtr={showAtr} />
                    </div>
                  )}
                </div>
                <div className="items-center justify-center mt-4">
                  {profitOrLoss > 0 && fromDate !== null && toDate !== null && (
                    <div className="flex-1  items-center justify-center mt-4">
                      <div>
                        <span className={`${Typo.p5} primary-text-color`}>{t('pnl.win')}:</span>
                        <span className={`${Typo.p5} positive font-bold underline`}>
                          {formatAmountV2(profitOrLoss, 2)}$
                        </span>
                      </div>
                    </div>
                  )}

                  {profitOrLoss < 0 && fromDate !== null && toDate !== null && (
                    <div className="flex-1 items-center justify-center mt-4">
                      <div>
                        <span className={`${Typo.p5} primary-text-color`}>{t('pnl.lose')}:</span>
                        <span className={`${Typo.p5} negative font-bold underline`}>
                          {formatAmountV2(Math.abs(profitOrLoss), 2)}$
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* <div className="flex-1 flex-col items-center justify-center mt-4">
                {profitOrLoss > 0 && fromDate !== null && toDate !== null && (
                  <div className="flex-1 flex-col items-center justify-center mt-4">
                    <div>
                      <span className={`${Typo.p5} primary-text-color`}>{t('pnl.win')}:</span>
                      <span className={`${Typo.p5} positive font-bold underline`}>
                        {formatAmountV2(profitOrLoss, 2)}$
                      </span>
                    </div>
                  </div>
                )}

                {profitOrLoss < 0 && fromDate !== null && toDate !== null && (
                  <div className="flex-1 flex-col items-center justify-center mt-4">
                    <div>
                      <span className={`${Typo.p5} primary-text-color`}>{t('pnl.lose')}:</span>
                      <span className={`${Typo.p5} negative font-bold underline`}>
                        {formatAmountV2(Math.abs(profitOrLoss), 2)}$
                      </span>
                    </div>
                  </div>
                )}
              </div> */}
            </div>
          ) : (
            <div className="d-flex flex-column">
              <div className="w-full flex flex-row items-center justify-between gap-4">
                <div className="flex flex-row items-center justify-between pnl-container w-1/2">
                  <span className={`primary-text-color ${Typo.p5} font-bold pnl-title`}>
                    {t('pnl.title')} :
                  </span>
                  {pnl && <ProfitAndLoss pnlType="TODAY" pnl={pnl} />}
                </div>
                <div className="flex flex-row items-center justify-between pnl-container w-1/2">
                  <div className="flex flex-row items-center">
                    <span className={`primary-text-color ${Typo.p5} font-bold pnl-title`}>
                      ATR :
                    </span>
                    <button
                      className="hidden-show-button primary-text-color"
                      onClick={handleShowAtr}
                    >
                      {showAtr ? <BsEyeSlash /> : <BsEye />}
                    </button>
                  </div>
                  {atr && <ProfitAndLoss pnlType="ATR" pnl={atr} showAtr={showAtr} />}
                </div>
              </div>
              <div className="w-full  items-center justify-between gap-4">
                {profitOrLoss > 0 && fromDate !== null && toDate !== null && (
                  <div className="flex-1  items-center justify-center mt-4">
                    <div>
                      <span className={`${Typo.p5} primary-text-color`}>{t('pnl.win')}:</span>
                      <span className={`${Typo.p5} positive font-bold underline`}>
                        {formatAmountV2(profitOrLoss, 2)}$
                      </span>
                    </div>
                  </div>
                )}

                {profitOrLoss < 0 && fromDate !== null && toDate !== null && (
                  <div className="flex-1  items-center justify-center mt-4">
                    <div>
                      <span className={`${Typo.p5} primary-text-color`}>{t('pnl.lose')}:</span>
                      <span className={`${Typo.p5} negative font-bold underline`}>
                        {formatAmountV2(Math.abs(profitOrLoss), 2)}$
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          <div className="my-bets-date-picker">
            <RangePicker
              format="DD/MM/YYYY"
              disabledDate={(current) =>
                fromDate !== ''
                  ? current.isBefore(moment(fromDate).startOf('day')) ||
                    current.isAfter(maxDate.endOf('day'))
                  : current.isBefore(minToDate) || current.isAfter(maxDate.endOf('day'))
              }
              // value={
              //   fromDate && toDate
              //     ? [moment(fromDate, 'YYYY-MM-DD'), moment(toDate, 'YYYY-MM-DD')]
              //     : null
              // }
              onChange={handleDateChange}
              mode={['date', 'date']}
              panelRender={(panel) => (
                <div style={{ display: 'inline-block', minWidth: 280 }}>{panel}</div>
              )}
            />
          </div>
        </div>
      )}
      <div className={`my-bets-list ${currentTab === 1 ? 'active-list' : ''}`}>
        {bets ? (
          <>
            {bets.length !== 0 ? (
              <>
                {bets.map((bet) =>
                  bet.type === 'SINGLE' ? (
                    // <MyBetSingle tab={currentTab} key={`my-bet-single-${bet.id}`} bet={bet} />
                    <MybetSingleItem tab={currentTab} key={`my-bet-single-${bet.id}`} bet={bet} />
                  ) : bet.type === 'TEASER' ? (
                    // <MyBetTeaser tab={currentTab} key={`my-bet-combo-${bet.id}`} bet={bet} />
                    <MyBetTeaserItem tab={currentTab} key={`my-bet-combo-${bet.id}`} bet={bet} />
                  ) : (
                    // <MyBetCombo tab={currentTab} key={`my-bet-combo-${bet.id}`} bet={bet} />
                    <MyBetComboItem tab={currentTab} key={`my-bet-combo-${bet.id}`} bet={bet} />
                  ),
                )}
              </>
            ) : (
              <div className="my-bets-empty">
                <EmptyTableIcon />
              </div>
            )}
          </>
        ) : (
          <div className="my-bets-list__spinner">
            <LoadingSpinner />
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBetsForm;
